// frontend/src/samkalp-faculty/liveSessions/HostLivePage.js
//
// Full-bleed broadcast console (bypasses dashboard chrome, same precedent
// as QuizMonitorPage). Joins the Agora channel as host (mode:'live'),
// publishes mic (+ optional camera), shows a live Q&A panel and a
// countdown to max_end_at (scheduled duration + the 30-min extension).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { Button, Spinner, Alert, Badge, ListGroup, Form } from 'react-bootstrap';
import {
    getLiveSessionFaculty, getHostToken, endLiveSession, listQuestionsFaculty, answerQuestion,
    getRelayStatus, pauseLiveSession, resumeLiveSession,
} from '../../api/liveSessionsApi';
import '../../liveSessionBroadcast.css';

// Mirrors RELAY_STATUSES in app/services/live_session_service.py.
const RELAY_STATUS_LABEL = {
    not_requested: null, starting: 'Dubbing: Preparing',
    running: 'Dubbing: Live', degraded: 'Dubbing: Degraded',
    start_failed: 'Dubbing: Unavailable',
    stop_requested: 'Dubbing: Stopping', stopping: 'Dubbing: Stopping',
    stopped: 'Dubbing: Stopped', stop_failed: 'Dubbing: Stop Failed',
};
const RELAY_STATUS_VARIANT = {
    starting: 'warning', running: 'success', degraded: 'warning',
    start_failed: 'danger', stop_requested: 'secondary', stopping: 'secondary',
    stopped: 'secondary', stop_failed: 'danger',
};

const NAVY = '#092957';
const GOLD = '#f1a51c';

const decodeToken = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
};

const formatCountdown = (ms) => {
    if (ms <= 0) return '00:00:00';
    const totalSec = Math.floor(ms / 1000);
    const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const HostLivePage = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('access_token');
    const slug = decodeToken(token)?.tenant_slug;
    const clientRef = useRef(null);
    const micTrackRef = useRef(null);
    const camTrackRef = useRef(null);
    const videoElRef = useRef(null);

    const [session, setSession] = useState(null);
    const [joined, setJoined] = useState(false);
    const [camOn, setCamOn] = useState(false);
    const [micMuted, setMicMuted] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [answerDrafts, setAnswerDrafts] = useState({});
    const [relayStatus, setRelayStatus] = useState(null);
    const [recordingPaused, setRecordingPaused] = useState(false);
    const camOnBeforePauseRef = useRef(false);

    useEffect(() => {
        getLiveSessionFaculty(slug, sessionId).then(setSession).catch(() => setError('Could not load session details.'));
    }, [slug, sessionId]);

    useEffect(() => {
        if (!session?.max_end_at) return undefined;
        const tick = () => setCountdown(new Date(session.max_end_at).getTime() - Date.now());
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [session]);

    const loadQuestions = useCallback(() => {
        listQuestionsFaculty(slug, sessionId).then(setQuestions).catch(() => {});
    }, [slug, sessionId]);

    useEffect(() => {
        loadQuestions();
        const interval = setInterval(loadQuestions, 6000);
        return () => clearInterval(interval);
    }, [loadQuestions]);

    useEffect(() => {
        if (!session?.target_languages?.length) return undefined;
        const poll = () => getRelayStatus(slug, sessionId).then(r => setRelayStatus(r.relay_status)).catch(() => {});
        poll();
        const interval = setInterval(poll, 12000);
        return () => clearInterval(interval);
    }, [slug, sessionId, session]);

    const joinChannel = useCallback(async () => {
        setError('');
        try {
            const { agora } = await getHostToken(slug, sessionId);
            const client = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
            await client.setClientRole('host');
            await client.join(agora.appId, agora.channel, agora.token, agora.uid || null);
            const micTrack = await AgoraRTC.createMicrophoneAudioTrack();
            await client.publish([micTrack]);
            clientRef.current = client;
            micTrackRef.current = micTrack;
            setJoined(true);
            setMicMuted(false);
        } catch (err) {
            setError(err?.message || 'Could not join the broadcast. Check mic permissions and try again.');
        }
    }, [slug, sessionId]);

    const toggleMic = async () => {
        if (!micTrackRef.current) return;
        const nextMuted = !micMuted;
        // setEnabled() stops sending audio frames without unpublishing/renegotiating
        // the track — the correct way to mute (vs. closing/republishing the track).
        await micTrackRef.current.setEnabled(!nextMuted);
        setMicMuted(nextMuted);
    };

    const toggleCamera = async () => {
        if (!clientRef.current) return;
        if (camOn) {
            // Closing a track does NOT auto-unpublish it — the client's
            // internal state still considers a video track published, so
            // a later publish([newCamTrack]) throws
            // CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS. Must unpublish first.
            if (camTrackRef.current) {
                await clientRef.current.unpublish([camTrackRef.current]);
                camTrackRef.current.close();
            }
            camTrackRef.current = null;
            setCamOn(false);
            return;
        }
        try {
            const camTrack = await AgoraRTC.createCameraVideoTrack();
            camTrackRef.current = camTrack;
            await clientRef.current.publish([camTrack]);
            camTrack.play(videoElRef.current);
            setCamOn(true);
        } catch (err) {
            setError('Could not start camera: ' + (err?.message || 'unknown error'));
        }
    };

    const handlePauseBroadcast = async () => {
        setError('');
        try {
            camOnBeforePauseRef.current = camOn;
            if (micTrackRef.current) await micTrackRef.current.setEnabled(false);
            if (camOn && camTrackRef.current) {
                // Same unpublish-before-close requirement as toggleCamera —
                // resume creates a fresh camera track, which would collide
                // with a stale-but-still-published one otherwise.
                await clientRef.current?.unpublish([camTrackRef.current]);
                camTrackRef.current.close();
                camTrackRef.current = null;
                setCamOn(false);
            }
            await pauseLiveSession(slug, sessionId);
            setRecordingPaused(true);
        } catch (err) {
            setError('Could not pause the broadcast: ' + (err?.response?.data?.detail || err?.message || 'unknown error'));
        }
    };

    const handleResumeBroadcast = async () => {
        setError('');
        try {
            if (micTrackRef.current) await micTrackRef.current.setEnabled(!micMuted);
            if (camOnBeforePauseRef.current && clientRef.current) {
                const camTrack = await AgoraRTC.createCameraVideoTrack();
                camTrackRef.current = camTrack;
                await clientRef.current.publish([camTrack]);
                camTrack.play(videoElRef.current);
                setCamOn(true);
            }
            await resumeLiveSession(slug, sessionId);
            setRecordingPaused(false);
        } catch (err) {
            setError('Could not resume the broadcast: ' + (err?.response?.data?.detail || err?.message || 'unknown error'));
        }
    };

    const leaveChannel = useCallback(async () => {
        micTrackRef.current?.close();
        camTrackRef.current?.close();
        await clientRef.current?.leave();
    }, []);

    useEffect(() => () => { leaveChannel(); }, [leaveChannel]);

    const handleEndSession = async () => {
        await leaveChannel();
        try {
            await endLiveSession(slug, sessionId);
        } catch (err) {
            // session may already be completed via auto-sweep — proceed regardless
        }
        navigate(`/samkalp-ias/faculty/live-sessions`);
    };

    const submitAnswer = async (questionId) => {
        const answer_text = answerDrafts[questionId];
        if (!answer_text) return;
        await answerQuestion(slug, sessionId, questionId, { answer_text });
        setAnswerDrafts(prev => ({ ...prev, [questionId]: '' }));
        loadQuestions();
    };

    if (!session) {
        return <div className="d-flex justify-content-center align-items-center vh-100"><Spinner animation="border" /></div>;
    }

    return (
        <div className="live-broadcast-shell" style={{ minHeight: '100vh', background: NAVY, color: '#fff', display: 'flex', flexDirection: 'column' }}>
            <header className="live-header" style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                <div>
                    <Badge bg="danger" className="me-2">LIVE</Badge>
                    {recordingPaused && <Badge bg="secondary" className="me-2">PAUSED</Badge>}
                    {relayStatus && relayStatus !== 'not_requested' && (
                        <Badge bg={RELAY_STATUS_VARIANT[relayStatus] || 'secondary'} className="me-2">
                            {RELAY_STATUS_LABEL[relayStatus] || relayStatus}
                        </Badge>
                    )}
                    <strong>{session.topic}</strong>
                    <div className="small" style={{ color: GOLD }}>
                        {session.faculty_name} {session.faculty_designation ? `— ${session.faculty_designation}` : ''}
                        {session.source_language && <span className="ms-2">Broadcasting in {session.source_language}</span>}
                    </div>
                </div>
                <div className="text-end">
                    <div className="small text-uppercase" style={{ color: '#9fb3d1' }}>Time remaining (incl. extension)</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{formatCountdown(countdown)}</div>
                </div>
            </header>

            {error && <Alert variant="danger" className="m-3 py-2 small">{error}</Alert>}

            <div className="live-body" style={{ flex: 1, padding: 24 }}>
                <div className="live-video-col" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className="live-video-frame">
                        <div ref={videoElRef} className="live-video-target" />
                        {!camOn && (
                            <span className="live-video-placeholder" style={{ color: '#5f7ba8' }}>
                                Audio-only broadcast{joined ? ' — live' : ''}
                            </span>
                        )}
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                        {!joined ? (
                            <Button style={{ background: GOLD, border: 'none', fontWeight: 700 }} onClick={joinChannel}>Start Broadcasting</Button>
                        ) : (
                            <>
                                <Button variant={micMuted ? 'warning' : 'outline-light'} onClick={toggleMic} disabled={recordingPaused}>{micMuted ? '🔇 Unmute Mic' : '🎙️ Mute Mic'}</Button>
                                <Button variant={camOn ? 'light' : 'outline-light'} onClick={toggleCamera} disabled={recordingPaused}>{camOn ? 'Stop Camera' : 'Start Camera'}</Button>
                                {recordingPaused ? (
                                    <Button variant="success" onClick={handleResumeBroadcast}>▶ Resume Broadcast</Button>
                                ) : (
                                    <Button variant="outline-light" onClick={handlePauseBroadcast}>⏸ Pause Broadcast</Button>
                                )}
                                <Button variant="danger" onClick={handleEndSession}>End Session</Button>
                            </>
                        )}
                    </div>
                    {micMuted && <p className="text-center small" style={{ color: GOLD }}>Your mic is muted — students can't hear you.</p>}
                </div>

                <div className="live-side-col" style={{ background: '#fff', color: '#111', borderRadius: 12, padding: 16, overflowY: 'auto', maxHeight: '75vh' }}>
                    <h6 className="fw-bold">Student Questions</h6>
                    {questions.length === 0 && <p className="small text-muted">No questions yet.</p>}
                    <ListGroup>
                        {questions.map(q => (
                            <ListGroup.Item key={q.id}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="small text-muted">{q.student_name || 'Student'}</div>
                                        <strong className="small">{q.question_text}</strong>
                                    </div>
                                    <Badge bg={q.status === 'answered' ? 'success' : 'warning'} text={q.status === 'answered' ? undefined : 'dark'}>{q.status}</Badge>
                                </div>
                                {q.status === 'answered' ? (
                                    <p className="small text-muted mb-0 mt-1">{q.answer_text}</p>
                                ) : (
                                    <div className="d-flex gap-2 mt-2">
                                        <Form.Control
                                            size="sm"
                                            placeholder="Type an answer..."
                                            value={answerDrafts[q.id] || ''}
                                            onChange={e => setAnswerDrafts(prev => ({ ...prev, [q.id]: e.target.value }))}
                                        />
                                        <Button size="sm" onClick={() => submitAnswer(q.id)}>Answer</Button>
                                    </div>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            </div>
        </div>
    );
};

export default HostLivePage;
